import {
  type ComponentType,
  createContext,
  type ReactNode,
  useContext,
  useEffect,
} from 'react';

export interface CapturedChart {
  component: string;
  props: Record<string, unknown>;
}

type RegisterFn = (scopeKey: string, capture: CapturedChart) => void;

interface ShareCaptureScopeValue {
  scopeKey: string;
  register: RegisterFn;
}

const ShareCaptureContext = createContext<ShareCaptureScopeValue | null>(null);

interface ShareCaptureScopeProps {
  scopeKey: string;
  register: RegisterFn;
  children: ReactNode;
}

/**
 * Provides the scope (item id) and register callback that `withShareCapture`
 * reads. Mount once per chart item in the dashboard render tree. When absent,
 * wrapped chart components render as no-op pass-throughs.
 */
export const ShareCaptureScope = ({
  scopeKey,
  register,
  children,
}: ShareCaptureScopeProps) => (
  <ShareCaptureContext.Provider value={{ scopeKey, register }}>
    {children}
  </ShareCaptureContext.Provider>
);

/**
 * Wraps a chart component so that, when rendered inside a `ShareCaptureScope`,
 * it registers its `{ component, props }` into the scope's store on mount and
 * on prop change. When no scope is present, renders unchanged with no cost
 * beyond a single context read.
 */
function withShareCapture<P extends object>(
  Component: ComponentType<P>,
  name: string
): ComponentType<P> {
  const Wrapped = (props: P) => {
    const scope = useContext(ShareCaptureContext);
    const cleaned = scope ? toJSONSafe(props) : null;
    const serialized = cleaned === null ? '' : JSON.stringify(cleaned);

    // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
    useEffect(() => {
      if (!scope || cleaned === null) return;
      // Register the cleaned (JSON-safe) props rather than the live reference.
      // Otherwise render-prop functions, symbols, and undefineds sit in the
      // store until `JSON.stringify(payload)` silently drops them on the wire,
      // producing a snapshot whose props no longer match what we "captured".
      scope.register(scope.scopeKey, {
        component: name,
        props: cleaned as Record<string, unknown>,
      });
      // `serialized` is the deterministic fingerprint of `cleaned`; depending
      // on `cleaned` directly would re-fire on every render because the object
      // identity changes each time.
    }, [scope, serialized]);

    return <Component {...props} />;
  };
  Wrapped.displayName = `withShareCapture(${name})`;
  return Wrapped;
}

/**
 * Wrap a chart component while preserving its call-site signature (including
 * generic type parameters). Use this when building wrapped exports from the
 * chart registry — it hides the generic-preserving cast so callsites look
 * identical to the unwrapped component.
 */
export const wrapChart = <T extends ComponentType<never>>(
  Component: T,
  name: string
): T =>
  withShareCapture(
    Component as unknown as ComponentType<Record<string, unknown>>,
    name
  ) as unknown as T;

/**
 * Strip functions, symbols, undefined, and circular references from a value
 * so the result matches what `JSON.stringify` would produce on the wire.
 * DOM nodes and refs to them are omitted as they cause `JSON.stringify` to throw RangeError.
 */
const toJSONSafe = (value: unknown): unknown => {
  const seen = new WeakSet<object>();
  const isDomNode = (o: object): boolean =>
    typeof Node !== 'undefined' && o instanceof Node;
  const walk = (v: unknown): unknown => {
    if (v === null) return null;
    if (typeof v !== 'object') {
      if (typeof v === 'function' || typeof v === 'symbol') return undefined;
      return v;
    }
    if (isDomNode(v as object)) return undefined;
    if (seen.has(v as object)) return undefined;
    seen.add(v as object);
    if (Array.isArray(v)) return v.map(walk);
    const out: Record<string, unknown> = {};
    Object.keys(v as Record<string, unknown>)
      .sort()
      .forEach((k) => {
        const cleaned = walk((v as Record<string, unknown>)[k]);
        if (cleaned !== undefined) out[k] = cleaned;
      });
    return out;
  };
  return walk(value);
};
